/**
 * Session Analytics Card
 * Displays login method statistics (Google vs Email)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart3, Users, Mail, Chrome, TrendingUp, RefreshCw, 
  Calendar, Clock
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

import { API } from "../lib/api";

const SessionAnalyticsCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/session-analytics`, {
        withCredentials: true
      });
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load session analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const totalLogins = stats.logins?.total || 0;
  const googleLogins = stats.logins?.google || 0;
  const emailLogins = stats.logins?.email || 0;
  const legacyLogins = stats.logins?.legacy || 0;
  
  const googlePercent = totalLogins > 0 ? Math.round((googleLogins / totalLogins) * 100) : 0;
  const emailPercent = totalLogins > 0 ? Math.round((emailLogins / totalLogins) * 100) : 0;

  return (
    <Card data-testid="session-analytics-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-600" />
              Login Analytics
            </CardTitle>
            <CardDescription>Track how users sign in to your app</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-stone-50 rounded-lg text-center">
            <Users className="w-6 h-6 text-stone-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-stone-900">{stats.total_users}</p>
            <p className="text-xs text-stone-500">Total Users</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg text-center">
            <Chrome className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{stats.users_google_only}</p>
            <p className="text-xs text-blue-600">Google Only</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg text-center">
            <Mail className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-700">{stats.users_with_password}</p>
            <p className="text-xs text-amber-600">With Password</p>
          </div>
        </div>

        {/* Login Method Breakdown */}
        <div className="space-y-4">
          <h4 className="font-semibold text-stone-700 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Login Method Breakdown
          </h4>
          
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Chrome className="w-4 h-4 text-blue-600" /> Google
                </span>
                <span className="font-medium">{googleLogins} ({googlePercent}%)</span>
              </div>
              <Progress value={googlePercent} className="h-2 bg-blue-100" />
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-600" /> Email/Password
                </span>
                <span className="font-medium">{emailLogins} ({emailPercent}%)</span>
              </div>
              <Progress value={emailPercent} className="h-2 bg-amber-100" />
            </div>

            {legacyLogins > 0 && (
              <div className="text-xs text-stone-500 mt-2">
                + {legacyLogins} sessions from before tracking was added
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-3">
          <h4 className="font-semibold text-stone-700 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Recent Activity
          </h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xl font-bold text-green-700">{stats.recent?.last_24h || 0}</p>
              <p className="text-xs text-green-600">Last 24h</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xl font-bold text-blue-700">{stats.recent?.last_7d || 0}</p>
              <p className="text-xs text-blue-600">Last 7 days</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-xl font-bold text-purple-700">{stats.recent?.last_30d || 0}</p>
              <p className="text-xs text-purple-600">Last 30 days</p>
            </div>
          </div>
        </div>

        {/* Daily Chart */}
        {stats.daily_breakdown && stats.daily_breakdown.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-stone-700 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Last 7 Days
            </h4>
            <div className="flex items-end gap-1 h-24">
              {stats.daily_breakdown.map((day, idx) => {
                const maxLogins = Math.max(...stats.daily_breakdown.map(d => d.total), 1);
                const height = (day.total / maxLogins) * 100;
                const googleHeight = day.total > 0 ? (day.google / day.total) * height : 0;
                const emailHeight = day.total > 0 ? (day.email / day.total) * height : 0;
                
                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full flex flex-col justify-end rounded-t"
                      style={{ height: '80px' }}
                    >
                      <div 
                        className="w-full bg-blue-400 rounded-t"
                        style={{ height: `${googleHeight}%` }}
                        title={`Google: ${day.google}`}
                      />
                      <div 
                        className="w-full bg-amber-400"
                        style={{ height: `${emailHeight}%` }}
                        title={`Email: ${day.email}`}
                      />
                    </div>
                    <span className="text-xs text-stone-500">
                      {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-400 rounded" /> Google
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-amber-400 rounded" /> Email
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionAnalyticsCard;
